import { useState, useMemo } from 'react';
import { keyBy, omit } from 'lodash-es';
import { Selection } from '@ds';
import { toast } from 'react-toastify';
import { BulkAssignmentCase } from '@/api/apis/case';

type UseBulkAssignmentSelectionProps = {
  data: BulkAssignmentCase[] | undefined;
  disabledKeys?: string[];
};

const CASES_LIMIT = 50;

export const useBulkAssignmentSelection = ({ data = [], disabledKeys = [] }: UseBulkAssignmentSelectionProps) => {
  const [selectedCases, setSelectedCases] = useState<Record<string, BulkAssignmentCase>>({});

  const selectedKeysOnPage = useMemo(() => {
    const caseIdsOnPage = new Set(data.map((d) => d.caseId));
    return new Set(Object.keys(selectedCases).filter((id) => caseIdsOnPage.has(id)));
  }, [selectedCases, data]);

  const showMaxSelectionToast = () => {
    toast.warning(`You can select up to ${CASES_LIMIT} cases for bulk assignment.`);
  };

  const handleCaseSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      const currentSelectedCount = Object.keys(selectedCases).length;
      const availableSlots = CASES_LIMIT - currentSelectedCount;
      if (availableSlots <= 0) {
        showMaxSelectionToast();
        return;
      }

      const validData = data.filter((item) => !disabledKeys.includes(item.caseId));
      const casesToAdd = validData.slice(0, availableSlots);
      const allCases = keyBy(casesToAdd, 'caseId');
      setSelectedCases({ ...selectedCases, ...allCases });
    } else {
      const keysSet = keys as Set<string>;
      const idsInData = data.map((d) => d.caseId);
      const preservedCases = omit(selectedCases, idsInData);
      const newSelection = keyBy(
        data.filter((item) => keysSet.has(item.caseId)),
        'caseId',
      );

      const totalSelected = Object.keys(preservedCases).length + Object.keys(newSelection).length;
      if (totalSelected > CASES_LIMIT) {
        showMaxSelectionToast();
        return;
      }

      setSelectedCases({ ...preservedCases, ...newSelection });
    }
  };

  const handleRemove = (key: string) => {
    setSelectedCases((prev) => {
      const newCases = { ...prev };
      delete newCases[key];
      return newCases;
    });
  };

  const selectedItems = useMemo(() => Object.values(selectedCases), [selectedCases]);

  return {
    selectedCases,
    setSelectedCases,
    selectedKeysOnPage,
    handleCaseSelectionChange,
    handleRemove,
    selectedItems,
  };
};
